import { useAuth0 } from '@auth0/auth0-react';
import CustomerHandler from 'handlers/customer.handler';
import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from 'store/hook';
import { type Customer } from 'types/customer.types';

interface CustomerSelectorOption {
  id: string;
  display: string;
}

export const useCustomerList = (): {
  customerList: CustomerSelectorOption[];
  isLoading: boolean;
  error: Error | null;
  refetchCustomers: () => Promise<void>;
} => {
  const { isAuthenticated } = useAuth0();

  const customerHandler = new CustomerHandler();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const role = useAppSelector((state) => state.authReducer.role);

  const [customerList, setCustomerList] = useState<CustomerSelectorOption[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCustomers = useCallback(async () => {
    if (!isAuthenticated || !accessToken || !role) return;

    setIsLoading(true);
    setError(null);

    try {
      const customers: Customer[] = await customerHandler.getAll(false);

      if (!Array.isArray(customers)) {
        throw new Error('Customers response is not an array');
      }

      const sortedOptions: CustomerSelectorOption[] = customers
        .map((customer) => ({
          id: customer.customerId,
          display: customer.customerName,
        }))
        .sort((firstOption, secondOption) =>
          firstOption.display.localeCompare(secondOption.display, undefined, {
            sensitivity: 'base',
          })
        );

      setCustomerList(sortedOptions);
    } catch (caughtError) {
      console.error('Failed to fetch customer selector options', caughtError);
      setCustomerList([]);
      setError(caughtError as Error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, accessToken, role]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !role) return;
    void fetchCustomers();
  }, [role, accessToken, isAuthenticated]);

  return {
    customerList,
    isLoading,
    error,
    refetchCustomers: fetchCustomers,
  };
};
